import { useState } from 'react'
import type { MarketingSection } from '@mc/api'
import { Trans, useTranslation } from '@mc/i18n'
import CTA from 'components/CTA'
import { FilterPicker, Selection } from 'components/FilterPicker'
import { TRENDING as TRENDING_SLUG } from 'libs/utils/categories'
import { CourseTileCarousel } from './CourseTileCarousel'
import { useCategories, useCourseTiles } from './dataFetching'
import { LOCATION_CAT_EXP, trackCategoryClick } from './events'
import { useTrendingRow } from './useHasCatalogExplorer'

export type CatalogExplorerProps = {
  heading?: React.ReactNode
  openNewPage?: boolean
  showCTA?: boolean
  marketingRow?: MarketingSection
  prioritizeTiles?: boolean
}

export const CatalogExplorer = ({
  className,
  arialabelledBy = 'catalog-explorer-title',
  ...props
}: { className?: string; arialabelledBy?: string } & CatalogExplorerProps) => {
  const trendingRow = useTrendingRow()

  if (!trendingRow) return <></>

  return (
    <section className={className} aria-labelledby={arialabelledBy}>
      <CatalogExplorerComp content={trendingRow} {...props} />
    </section>
  )
}

const CatalogExplorerComp = ({
  content,
  heading,
  openNewPage,
  showCTA = true,
  prioritizeTiles,
}: { content: MarketingSection } & CatalogExplorerProps) => {
  const { t } = useTranslation('@mc/next')

  const categories = useCategories()
  const trendingItems = content.marketingSectionItems
  const [selectedCategorySlug, setSelectedCategorySlug] =
    useState<string>(TRENDING_SLUG)

  const { loading, error, courseTiles } = useCourseTiles({
    selectedCategorySlug,
    trendingItems,
  })

  const categorySelections: Array<Selection> = [
    ...[
      {
        slug: TRENDING_SLUG,
        name: t('home.catalogExplorer.trending'),
      } as Selection,
    ],
    ...(categories.map((c) => ({
      name: c.name,
      slug: c.slug,
      id: c.id,
    })) as Array<Selection>),
  ]

  const selectedCategory = categories.find(
    (c) => selectedCategorySlug === c.slug,
  )

  return (
    <>
      {heading || (
        <Heading>
          <Trans t={t} i18nKey='home.catalogExplorer.headings.explore'>
            <br />
          </Trans>
        </Heading>
      )}
      <FilterPicker
        selections={categorySelections}
        selectedSlug={selectedCategorySlug}
        setSelectedSlug={setSelectedCategorySlug}
        onSelect={trackCategoryClick}
      />
      <CourseTileCarousel
        category={selectedCategory}
        loading={loading || error}
        courseTiles={courseTiles}
        openNewPage={openNewPage}
        prioritizeTiles={prioritizeTiles}
      />

      {showCTA && (
        <div className='mc-mt-8 mc-text--center'>
          <CTA
            kind='secondary'
            trackProps={{
              action: t('home.catalogExplorer.exploreCta'),
              content_type: 'product',
              location: LOCATION_CAT_EXP,
            }}
          >
            {t('home.catalogExplorer.exploreCta')}
          </CTA>
        </div>
      )}
    </>
  )
}

const Heading = ({ children: text }: { children: React.ReactNode }) => (
  <h2
    className='mc-mt-11 mc-mb-10 mc-text-h1 mc-text--center'
    id='catalog-explorer-title'
  >
    {text}
  </h2>
)
